import React, { useState, useEffect } from 'react';
import { Product, ProductComment, UserProfile, WaitlistEntry } from '../types';
import { X, Info, Share2, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, MessageCircle, Send, ShieldAlert } from 'lucide-react';
import { recordProductView } from '../api/interactions';
import {
  createProductComment,
  deleteProductComment,
  getProductComments,
  updateProductComment,
} from '../api/product-comments';
import shareKakao from '../utils/shareKakao';
import {
  SHARE_TOKEN_PARAM,
  SHARED_PRODUCT_PARAM,
  createShareToken,
} from '../utils/shareTracking';
import WaitlistQuantityControl from './WaitlistQuantityControl';

interface ProductDetailModalProps {
  product: Product | null;
  storeId: string;
  storeName?: string;
  orderSheetId?: string;
  isOpen: boolean;
  onClose: () => void;
  onOrderNow?: (product: Product) => void;
  onShareCreated?: (product: Product, url: string, shareToken: string) => void;
  buyerProfile?: UserProfile & { id?: string };
  waitlistEntry?: WaitlistEntry;
  isWaitlistUpdating?: boolean;
  waitlistQuantity?: number;
  onSetWaitlistQuantity?: (quantity: number) => void;
  onToggleWaitlist?: (productId: string, quantity: number) => void;
}

const toAbsoluteImageUrl = (url?: string) => {
  if (!url) return new URL('/images/not_found.png', window.location.origin).toString();
  return new URL(url, window.location.origin).toString();
};

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  product,
  storeId,
  storeName,
  orderSheetId,
  isOpen,
  onClose,
  onOrderNow,
  onShareCreated,
  buyerProfile,
  waitlistEntry,
  isWaitlistUpdating = false,
  waitlistQuantity = 1,
  onSetWaitlistQuantity,
  onToggleWaitlist,
}) => {
  type CommentPage = 'detail' | 'comments' | 'write';
  const [activeImgIdx, setActiveImgIdx] = useState(0);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [viewerZoom, setViewerZoom] = useState(1);
  const [comments, setComments] = useState<ProductComment[]>([]);
  const [commentContent, setCommentContent] = useState('');
  const [replyTarget, setReplyTarget] = useState<ProductComment | null>(null);
  const [editingComment, setEditingComment] = useState<ProductComment | null>(null);
  const [commentPage, setCommentPage] = useState<CommentPage>('detail');
  const [isCommentsLoading, setIsCommentsLoading] = useState(false);
  const [isCommentSubmitting, setIsCommentSubmitting] = useState(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const customerCommentName = buyerProfile?.name?.trim() || '';

  useEffect(() => {
    if (isOpen && product?.id) {
      recordProductView(product.id);
    }
  }, [isOpen, product?.id]);

  useEffect(() => {
    if (!isOpen || !product?.id || !product.isCommentEnabled) {
      setComments([]);
      return;
    }

    setIsCommentsLoading(true);
    getProductComments(product.id)
      .then(setComments)
      .catch((error) => {
        console.error('Failed to load product comments', error);
        setComments([]);
      })
      .finally(() => setIsCommentsLoading(false));
  }, [isOpen, product?.id, product?.isCommentEnabled]);

  useEffect(() => {
    if (!isOpen) {
      setIsImageViewerOpen(false);
      setViewerZoom(1);
      setActiveImgIdx(0);
      setCommentContent('');
      setReplyTarget(null);
      setEditingComment(null);
      setCommentPage('detail');
      setComments([]);
    }
  }, [isOpen]);

  useEffect(() => {
    setCommentPage('detail');
    setCommentContent('');
    setReplyTarget(null);
    setEditingComment(null);
  }, [product?.id]);

  useEffect(() => {
    setViewerZoom(1);
  }, [activeImgIdx]);

  if (!isOpen || !product) return null;

  const stock = product.availableStock ?? product.totalStock ?? 0;
  const isSoldOut = stock === 0;
  const hasOptions = Boolean(product.options?.length);
  const canJoinWaitlist =
    isSoldOut && !hasOptions && !product.isClosed && Boolean(onToggleWaitlist);
  const carouselImages = (product.images?.length ? product.images : product.image ? [product.image] : [])
    .filter((img): img is string => Boolean(img));

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const scrollLeft = container.scrollLeft;
    const width = container.clientWidth;
    const newIdx = Math.round(scrollLeft / width);
    if (newIdx !== activeImgIdx) {
      setActiveImgIdx(newIdx);
    }
  };

  const scrollToImage = (index: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: index * scrollRef.current.clientWidth,
        behavior: 'smooth'
      });
    }
  };

  const handleNext = () => scrollToImage((activeImgIdx + 1) % carouselImages.length);
  const handlePrev = () => scrollToImage((activeImgIdx - 1 + carouselImages.length) % carouselImages.length);

  const openImageViewer = (index: number) => {
    setActiveImgIdx(index);
    setViewerZoom(1);
    setIsImageViewerOpen(true);
  };

  const handleViewerNext = () => {
    if (carouselImages.length > 1) {
      setActiveImgIdx((activeImgIdx + 1) % carouselImages.length);
    }
  };

  const handleViewerPrev = () => {
    if (carouselImages.length > 1) {
      setActiveImgIdx((activeImgIdx - 1 + carouselImages.length) % carouselImages.length);
    }
  };

  const handleShare = async () => {
    const shareUrl = new URL(`/store/${encodeURIComponent(storeId)}`, window.location.origin);
    shareUrl.searchParams.set('productId', product.id);

    if (orderSheetId) {
      shareUrl.searchParams.set('orderSheetId', orderSheetId);
    }

    // 공유자 → 유입/주문 어트리뷰션용 불투명 토큰 (userId는 URL에 노출하지 않음)
    const shareToken = createShareToken();
    shareUrl.searchParams.set(SHARE_TOKEN_PARAM, shareToken);
    shareUrl.searchParams.set(SHARED_PRODUCT_PARAM, product.id);

    const content = `${storeId}-${product.id}`;
    const didOpenKakaoShare = shareKakao({
      title: storeName ? `${storeName} ${product.name}` : product.name,
      description:
        product.description?.trim() ||
        `${storeName || '공동구매'}에서 ${product.name} 상품을 확인해보세요.`,
      imageUrl: toAbsoluteImageUrl(carouselImages[activeImgIdx] || product.image),
      url: shareUrl.toString(),
      campaign: 'product-share',
      content,
    });

    onShareCreated?.(product, shareUrl.toString(), shareToken);

    if (didOpenKakaoShare) {
      return;
    }

    try {
      await navigator.clipboard.writeText(shareUrl.toString());
      alert('카카오 공유를 사용할 수 없어 상품 링크를 복사했습니다.');
    } catch (error) {
      console.error('Failed to copy product share link', error);
      alert('카카오 공유를 사용할 수 없습니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const handleSubmitComment = async () => {
    if (!product.isCommentEnabled) {
      return;
    }

    const content = commentContent.trim();

    if (!customerCommentName) {
      alert('내 정보에서 이름을 먼저 입력해주세요.');
      return;
    }

    if (!content) {
      alert('댓글 내용을 입력해주세요.');
      return;
    }

    try {
      setIsCommentSubmitting(true);
      if (editingComment) {
        const updatedComment = await updateProductComment(editingComment.id, {
          content,
        });
        setComments((current) =>
          current.map((comment) =>
            comment.id === updatedComment.id ? updatedComment : comment,
          ),
        );
        setCommentContent('');
        setReplyTarget(null);
        setEditingComment(null);
        setCommentPage('comments');
        return;
      }

      const createdComment = await createProductComment(product.id, {
        authorName: customerCommentName,
        content,
        parentId: replyTarget?.id,
      });
      setComments((current) => [createdComment, ...current]);
      setCommentContent('');
      setReplyTarget(null);
      setEditingComment(null);
      setCommentPage('comments');
    } catch (error) {
      console.error('Failed to submit product comment', error);
      alert('댓글 등록에 실패했습니다.');
    } finally {
      setIsCommentSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('이 댓글을 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteProductComment(commentId);
      setComments((current) =>
        current.filter(
          (comment) => comment.id !== commentId && comment.parentId !== commentId,
        ),
      );
    } catch (error) {
      console.error('Failed to delete product comment', error);
      alert('댓글 삭제에 실패했습니다.');
    }
  };

  const rootComments = comments.filter((comment) => !comment.parentId);
  const repliesByParentId = comments
    .filter((comment) => comment.parentId)
    .reduce<Record<string, ProductComment[]>>((acc, comment) => {
      const parentId = comment.parentId;
      if (!parentId) return acc;
      acc[parentId] = [...(acc[parentId] || []), comment].sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      return acc;
    }, {});
  const previewComments = rootComments.slice(0, 10);
  const hasMoreComments = rootComments.length > previewComments.length;
  const isMyComment = (comment: ProductComment) =>
    Boolean(buyerProfile?.id && comment.userId === buyerProfile.id);
  const renderCommentCard = (comment: ProductComment) => (
    <div key={comment.id} className="rounded-xl border border-gray-100 bg-white p-4">
      <div className="mb-1 flex items-center justify-between gap-2">
        <span className="min-w-0 truncate text-xs font-black text-gray-900">
          {comment.authorName}
        </span>
        <span className="shrink-0 text-[10px] font-bold text-gray-400">
          {new Date(comment.createdAt).toLocaleDateString('ko-KR', {
            month: 'numeric',
            day: 'numeric',
          })}
        </span>
      </div>
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-600">
        {comment.content}
      </p>
      <div className="mt-3 flex items-center gap-3">
        {!comment.parentId && (
          <button
            type="button"
            onClick={() => {
              setReplyTarget(comment);
              setEditingComment(null);
              setCommentContent('');
              setCommentPage('write');
            }}
            className="text-[11px] font-black text-emerald-600"
          >
            답댓글
          </button>
        )}
        {isMyComment(comment) && (
          <>
            <button
              type="button"
              onClick={() => {
                setEditingComment(comment);
                setReplyTarget(null);
                setCommentContent(comment.content);
                setCommentPage('write');
              }}
              className="text-[11px] font-black text-gray-500"
            >
              수정
            </button>
            <button
              type="button"
              onClick={() => handleDeleteComment(comment.id)}
              className="text-[11px] font-black text-red-500"
            >
              삭제
            </button>
          </>
        )}
      </div>
      {!comment.parentId && repliesByParentId[comment.id]?.length > 0 && (
        <div className="mt-3 space-y-2 border-l-2 border-emerald-100 pl-3">
          {repliesByParentId[comment.id].map((reply) => (
            <div key={reply.id} className="rounded-xl bg-emerald-50/60 p-3">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="min-w-0 truncate text-xs font-black text-gray-900">
                  {reply.authorName}
                </span>
                <span className="shrink-0 text-[10px] font-bold text-gray-400">
                  {new Date(reply.createdAt).toLocaleDateString('ko-KR', {
                    month: 'numeric',
                    day: 'numeric',
                  })}
                </span>
              </div>
              <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-600">
                {reply.content}
              </p>
              {isMyComment(reply) && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setEditingComment(reply);
                      setReplyTarget(null);
                      setCommentContent(reply.content);
                      setCommentPage('write');
                    }}
                    className="text-[11px] font-black text-gray-500"
                  >
                    수정
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteComment(reply.id)}
                    className="text-[11px] font-black text-red-500"
                  >
                    삭제
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[3000] flex items-center justify-center p-0 sm:p-4">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={onClose}
      />

      <div className="relative w-full h-full sm:h-auto max-w-md bg-white sm:rounded-[24px] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200">
        <div className="absolute top-4 left-4 right-4 flex justify-between z-20 pointer-events-none">
          <button
            onClick={() => {
              if (commentPage !== 'detail') {
                setReplyTarget(null);
                setEditingComment(null);
                setCommentContent('');
                setCommentPage('detail');
                return;
              }
              onClose();
            }}
            className="w-10 h-10 rounded-full bg-white/80 backdrop-blur shadow-md flex items-center justify-center hover:bg-white transition-colors pointer-events-auto"
          >
            <ChevronLeft size={24} className="text-gray-800" />
          </button>
          {commentPage === 'detail' && (
            <button
              type="button"
              onClick={handleShare}
              className="w-10 h-10 rounded-full bg-white/80 backdrop-blur shadow-md flex items-center justify-center hover:bg-white transition-colors pointer-events-auto"
              aria-label="카카오톡으로 상품 공유"
            >
              <Share2 size={18} className="text-gray-800" />
            </button>
          )}
        </div>

        <div className="overflow-y-auto no-scrollbar flex-1">
          {commentPage === 'detail' ? (
            <>
              {/* Carousel */}
              <div className="aspect-square w-full relative bg-gray-100 group overflow-hidden">
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className="flex w-full h-full overflow-x-auto snap-x snap-mandatory no-scrollbar"
            >
              {carouselImages.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => openImageViewer(idx)}
                  className="relative w-full h-full snap-center shrink-0 flex items-center justify-center bg-black/5 overflow-hidden cursor-zoom-in"
                  aria-label={`${product.name} 이미지 크게 보기`}
                >
                  {/* Blurred Background to fill gaps for non-square images */}
                  <img
                    src={img}
                    className="absolute inset-0 w-full h-full object-cover blur-[20px] opacity-30 scale-110"
                    aria-hidden="true"
                  />
                  {/* Main Image - Contain to avoid cropping */}
                  <img
                    src={img}
                    alt={`${product.name} ${idx + 1}`}
                    className="relative z-10 w-full h-full object-contain transition-opacity duration-300"
                  />
                  <span className="absolute right-4 bottom-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white shadow-lg backdrop-blur">
                    <ZoomIn size={18} />
                  </span>
                </button>
              ))}
            </div>

            {carouselImages.length > 1 && (
              <>
                <button
                  onClick={handlePrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/30 hover:bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm hidden sm:flex items-center justify-center z-20"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={handleNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/30 hover:bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-all backdrop-blur-sm hidden sm:flex items-center justify-center z-20"
                >
                  <ChevronRight size={20} />
                </button>
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                  {carouselImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => scrollToImage(i)}
                      className={`h-1.5 rounded-full transition-all shadow-sm ${i === activeImgIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/50'}`}
                    />
                  ))}
                </div>
                {/* Visual swipe hint for mobile */}
                <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 flex justify-between px-4 pointer-events-none sm:hidden">
                  <div className="bg-white/10 rounded-full p-1"><ChevronLeft size={16} className="text-white/20" /></div>
                  <div className="bg-white/10 rounded-full p-1"><ChevronRight size={16} className="text-white/20" /></div>
                </div>
              </>
            )}

            {isSoldOut && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="text-white text-lg font-bold border-2 border-white px-4 py-2 rounded-lg">품절된 상품입니다</span>
              </div>
            )}
              </div>

              <div className="p-6">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {product.tags?.map(tag => (
                <span key={tag} className="bg-emerald-100 text-emerald-700 text-[10px] font-black px-2 py-0.5 rounded uppercase">
                  #{tag}
                </span>
              ))}
            </div>

            <h2 className="text-xl font-black text-gray-900 leading-tight mb-4">
              {product.parentId ? (
                // If it's a variant, we should ideally have the parent name.
                // Since parent name isn't directly on the product, we might need to pass it or 
                // use the split logic as fallback for legacy.
                product.name.includes('|') ? product.name.split('|')[0].trim() : product.name
              ) : (
                product.name.split('|')[0].trim()
              )}
            </h2>

            {product.isCustomerCancellationEnabled === false && (
              <div className="mb-5 flex items-start gap-2 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-orange-700">
                <ShieldAlert size={16} className="mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs font-black">주문 후 취소할 수 없는 상품입니다.</p>
                  <p className="mt-1 text-[10px] font-bold text-orange-600">
                    주문 내용을 확인한 뒤 구매해주세요.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-8 pb-4 border-b">
              <div className="flex flex-col">
                <span className="text-[11px] text-gray-400 font-bold uppercase mb-0.5">재고 현황</span>
                <span className={`text-sm font-black ${isSoldOut ? 'text-red-500' : 'text-emerald-600'}`}>
                  {isSoldOut
                    ? product.waitlistCount
                      ? `현재 품절 · ${product.waitlistCount}개 대기`
                      : '현재 품절'
                    : `${stock}개 구매 가능`}
                </span>
              </div>
              <span className="text-2xl font-black text-gray-900">{product.price.toLocaleString()}원</span>
            </div>

            <div className="space-y-6">
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Info size={16} className="text-emerald-500" />
                  <h3 className="text-sm font-bold text-gray-800">상품 상세 정보</h3>
                </div>
                <div className="whitespace-pre-line text-sm text-gray-600 leading-relaxed bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  {product.description || "상세 설명이 준비 중입니다. 잠시만 기다려 주세요."}
                </div>
              </section>

              {product.isCommentEnabled && (
                <section>
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <MessageCircle size={16} className="text-emerald-500" />
                      <h3 className="text-sm font-bold text-gray-800">상품 댓글</h3>
                    </div>
                    <span className="text-[11px] font-black text-gray-400">
                      {comments.length.toLocaleString()}개
                    </span>
                  </div>

                  <div className="space-y-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                    <button
                      type="button"
                      onClick={() => {
                        setReplyTarget(null);
                        setEditingComment(null);
                        setCommentContent('');
                        setCommentPage('write');
                      }}
                      className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-gray-900 text-xs font-black text-white transition-all active:scale-[0.98]"
                    >
                      <Send size={14} />
                      댓글쓰기
                    </button>

                    <div className="space-y-2">
                      {isCommentsLoading ? (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-[12px] font-bold text-gray-400">
                          댓글을 불러오는 중입니다...
                        </div>
                      ) : comments.length > 0 ? (
                        <>
                          {previewComments.map(renderCommentCard)}
                          {hasMoreComments && (
                            <button
                              type="button"
                              onClick={() => setCommentPage('comments')}
                              className="h-11 w-full rounded-xl border border-gray-200 bg-white text-xs font-black text-gray-600 transition-all hover:border-emerald-200 hover:text-emerald-600 active:scale-[0.98]"
                            >
                              댓글 더보기
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-6 text-center text-[12px] font-bold text-gray-400">
                          첫 댓글을 남겨보세요.
                        </div>
                      )}
                    </div>
                  </div>
                </section>
              )}
            </div>
              </div>
            </>
          ) : commentPage === 'comments' ? (
            <div className="min-h-full p-6 pt-20">
              <div className="mb-5 flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <MessageCircle size={18} className="text-emerald-500" />
                    <h2 className="text-xl font-black text-gray-900">상품 댓글</h2>
                  </div>
                  <p className="mt-1 text-xs font-bold text-gray-400">
                    {product.name} · 댓글 {comments.length.toLocaleString()}개
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setReplyTarget(null);
                    setEditingComment(null);
                    setCommentContent('');
                    setCommentPage('write');
                  }}
                  className="h-10 shrink-0 rounded-xl bg-gray-900 px-4 text-xs font-black text-white"
                >
                  댓글쓰기
                </button>
              </div>

              <div className="space-y-2 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                {isCommentsLoading ? (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center text-[12px] font-bold text-gray-400">
                    댓글을 불러오는 중입니다...
                  </div>
                ) : rootComments.length > 0 ? (
                  rootComments.map(renderCommentCard)
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center text-[12px] font-bold text-gray-400">
                    첫 댓글을 남겨보세요.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="min-h-full p-6 pt-20">
              <div className="mb-5">
                <div className="flex items-center gap-2">
                  <Send size={18} className="text-emerald-500" />
                  <h2 className="text-xl font-black text-gray-900">
                    {editingComment ? '댓글 수정' : replyTarget ? '답댓글쓰기' : '댓글쓰기'}
                  </h2>
                </div>
                <p className="mt-1 text-xs font-bold text-gray-400">
                  {product.name}
                </p>
              </div>

              <div className="space-y-3 rounded-2xl border border-gray-100 bg-gray-50 p-4">
                <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                  <p className="text-[10px] font-black uppercase text-gray-400">
                    작성자
                  </p>
                  <p className="mt-1 text-sm font-black text-gray-900">
                    {customerCommentName || '이름 없음'}
                  </p>
                </div>
                {(replyTarget || editingComment) && (
                  <div className="rounded-xl border border-emerald-100 bg-white px-4 py-3">
                    <p className="text-[10px] font-black uppercase text-emerald-500">
                      {editingComment ? '수정 대상' : '답댓글 대상'}
                    </p>
                    <p className="mt-1 truncate text-xs font-black text-gray-900">
                      {(editingComment || replyTarget)?.authorName}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs font-bold leading-relaxed text-gray-500">
                      {(editingComment || replyTarget)?.content}
                    </p>
                  </div>
                )}
                <textarea
                  className="min-h-[180px] w-full resize-none rounded-xl border border-gray-200 bg-white p-4 text-sm font-medium outline-none transition-colors focus:border-emerald-500"
                  placeholder={editingComment ? '수정할 댓글 내용을 입력해주세요.' : replyTarget ? '답댓글을 입력해주세요.' : '상품에 대해 궁금한 점이나 후기를 남겨주세요.'}
                  maxLength={500}
                  value={commentContent}
                  onChange={(e) => setCommentContent(e.target.value)}
                />
                <button
                  type="button"
                  onClick={handleSubmitComment}
                  disabled={isCommentSubmitting}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gray-900 text-sm font-black text-white transition-all active:scale-[0.98] disabled:bg-gray-300"
                >
                  <Send size={15} />
                  {isCommentSubmitting ? '저장 중' : editingComment ? '수정 완료' : replyTarget ? '답댓글 등록' : '댓글 등록'}
                </button>
              </div>
            </div>
          )}
        </div>

        {commentPage === 'detail' && (
          <div className="p-4 border-t bg-white">
          {canJoinWaitlist ? (
            <WaitlistQuantityControl
              quantity={waitlistQuantity}
              onQuantityChange={(quantity) => onSetWaitlistQuantity?.(quantity)}
              onToggle={() => onToggleWaitlist?.(product.id, waitlistQuantity)}
              waitlistEntry={waitlistEntry}
              waitingQuantity={product.waitlistCount}
              disabled={isWaitlistUpdating}
              variant="full"
            />
          ) : (
          <button
            onClick={() => {
              onOrderNow?.(product);
            }}
            disabled={isWaitlistUpdating || (isSoldOut && hasOptions)}
            className={`flex h-14 w-full items-center justify-center gap-2 rounded-2xl font-bold transition-all active:scale-[0.98] disabled:cursor-not-allowed ${isSoldOut && hasOptions
                ? 'bg-gray-100 text-gray-400'
                : 'bg-emerald-500 text-white shadow-lg shadow-emerald-100 hover:bg-emerald-600'
              }`}
          >
            {isWaitlistUpdating
              ? '처리 중입니다'
              : isSoldOut && hasOptions
                  ? '상품 목록에서 옵션별로 줄서기'
                  : '주문하기'}
          </button>
          )}
          </div>
        )}
      </div>

      {isImageViewerOpen && carouselImages[activeImgIdx] && (
        <div className="fixed inset-0 z-[4000] bg-black text-white animate-in fade-in duration-150">
          <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between pointer-events-none">
            <button
              type="button"
              onClick={() => setIsImageViewerOpen(false)}
              className="h-11 w-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 transition-colors pointer-events-auto"
              aria-label="이미지 크게 보기 닫기"
            >
              <X size={22} />
            </button>

            <div className="flex items-center gap-2 pointer-events-auto">
              {carouselImages.length > 1 && (
                <span className="rounded-full bg-white/15 px-3 py-2 text-xs font-bold backdrop-blur">
                  {activeImgIdx + 1} / {carouselImages.length}
                </span>
              )}
              <button
                type="button"
                onClick={() => setViewerZoom(viewerZoom === 1 ? 2 : 1)}
                className="h-11 w-11 rounded-full bg-white/15 backdrop-blur flex items-center justify-center hover:bg-white/25 transition-colors"
                aria-label={viewerZoom === 1 ? '이미지 확대' : '이미지 축소'}
              >
                {viewerZoom === 1 ? <ZoomIn size={20} /> : <ZoomOut size={20} />}
              </button>
            </div>
          </div>

          {carouselImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={handleViewerPrev}
                className="absolute left-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 backdrop-blur transition-colors hover:bg-white/25"
                aria-label="이전 이미지"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                onClick={handleViewerNext}
                className="absolute right-3 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/15 backdrop-blur transition-colors hover:bg-white/25"
                aria-label="다음 이미지"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          <div
            className="h-full w-full overflow-auto overscroll-contain px-0 py-20"
            onClick={() => setViewerZoom(viewerZoom === 1 ? 2 : 1)}
          >
            <div className="flex min-h-full min-w-full items-center justify-center">
              <img
                src={carouselImages[activeImgIdx]}
                alt={`${product.name} 크게 보기`}
                className="select-none"
                style={{
                  width: viewerZoom === 1 ? 'auto' : `${viewerZoom * 100}vw`,
                  maxWidth: viewerZoom === 1 ? '100vw' : 'none',
                  maxHeight: viewerZoom === 1 ? 'calc(100dvh - 10rem)' : 'none'
                }}
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={() => setViewerZoom(viewerZoom === 1 ? 2 : 1)}
              />
            </div>
          </div>

          {carouselImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
              {carouselImages.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImgIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === activeImgIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/40'}`}
                  aria-label={`${i + 1}번째 이미지 보기`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetailModal;
